import React from 'react'
import { 
  X, 
  User, 
  Calendar, 
  Clock, 
  Star,
  MessageSquare,
  Phone,
  Video,
  CheckCircle,
  XCircle,
  PlayCircle,
  AlertCircle
} from 'lucide-react'
import { cn, formatDate, formatTime } from '@/lib/utils'

interface SessionDetailModalProps {
  session: any
  isOpen: boolean
  onClose: () => void
}

export const SessionDetailModal: React.FC<SessionDetailModalProps> = ({
  session,
  isOpen,
  onClose
}) => {
  if (!isOpen || !session) return null

  const getStatusColor = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'ended': return 'bg-green-100 text-green-800'
      case 'active': return 'bg-blue-100 text-blue-800'
      case 'pending': return 'bg-yellow-100 text-yellow-800'
      case 'cancelled': return 'bg-red-100 text-red-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  const getStatusIcon = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'ended': return CheckCircle
      case 'active': return PlayCircle
      case 'pending': return Clock
      case 'cancelled': return XCircle
      default: return AlertCircle
    }
  }

  const getSessionTypeIcon = (type: string) => {
    switch (type?.toLowerCase()) {
      case 'chat': return MessageSquare
      case 'voice': return Phone
      case 'video': return Video
      default: return Calendar
    }
  }

  const formatDuration = (startedAt: string, endedAt?: string) => {
    if (!endedAt) return 'Ongoing'
    
    const start = new Date(startedAt)
    const end = new Date(endedAt)
    const durationMs = end.getTime() - start.getTime()
    const minutes = Math.floor(durationMs / (1000 * 60))
    
    if (minutes < 60) {
      return `${minutes} minutes`
    } else {
      const hours = Math.floor(minutes / 60)
      const remainingMinutes = minutes % 60
      return remainingMinutes > 0 ? `${hours}h ${remainingMinutes}m` : `${hours} hours`
    }
  }

  const StatusIcon = getStatusIcon(session.status)
  const TypeIcon = getSessionTypeIcon(session.sessionType)

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 transition-opacity" aria-hidden="true">
          <div className="absolute inset-0 bg-gray-500 opacity-75" onClick={onClose}></div>
        </div>

        <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>

        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
          <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
            <div className="flex items-start justify-between mb-4">
              <h3 className="text-lg leading-6 font-medium text-gray-900">
                Session Details
              </h3>
              <button
                onClick={onClose}
                className="bg-white rounded-md text-gray-400 hover:text-gray-600 focus:outline-none"
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            <div className="space-y-6">
              {/* Session Overview */}
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center space-x-2">
                    <TypeIcon className="h-5 w-5 text-blue-600" />
                    <h4 className="font-medium text-gray-900">
                      {session.sessionType?.toUpperCase() || 'Session'}
                    </h4>
                  </div>
                  <div className={cn(
                    'flex items-center space-x-1 px-2 py-1 rounded-full text-xs font-medium',
                    getStatusColor(session.status)
                  )}>
                    <StatusIcon className="h-3 w-3" />
                    <span>{session.status?.toUpperCase()}</span>
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-gray-500">Started:</span>
                    <p className="font-medium">
                      {formatDate(session.startedAt)} {formatTime(session.startedAt)}
                    </p>
                  </div>
                  {session.endedAt && (
                    <div>
                      <span className="text-gray-500">Ended:</span>
                      <p className="font-medium">
                        {formatDate(session.endedAt)} {formatTime(session.endedAt)}
                      </p>
                    </div>
                  )}
                  <div>
                    <span className="text-gray-500">Duration:</span>
                    <p className="font-medium">
                      {formatDuration(session.startedAt, session.endedAt)}
                    </p>
                  </div>
                  {session.rating && (
                    <div>
                      <span className="text-gray-500">Rating:</span>
                      <div className="flex items-center space-x-1">
                        <Star className="h-4 w-4 fill-yellow-400 text-yellow-400" />
                        <span className="font-medium">{session.rating}/5</span>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Student Information */}
              {session.student && (
                <div>
                  <h4 className="font-medium text-gray-900 mb-3 flex items-center">
                    <User className="h-4 w-4 mr-2" />
                    Student Information
                  </h4>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <div className="flex items-center space-x-3 mb-3">
                      {session.student.profilePicture ? (
                        <img 
                          src={session.student.profilePicture} 
                          alt={session.student.name}
                          className="h-12 w-12 rounded-full object-cover"
                        />
                      ) : (
                        <div className="h-12 w-12 bg-blue-100 rounded-full flex items-center justify-center">
                          <User className="h-6 w-6 text-blue-600" />
                        </div>
                      )}
                      <div>
                        <p className="font-medium text-gray-900">{session.student.name}</p>
                        {/* <p className="text-sm text-gray-600">{session.student.phoneNumber}</p> */}
                        {session.student.email && (
                          <p className="text-sm text-gray-600">{session.student.email}</p>
                        )}
                      </div>
                    </div>
                    
                    {session.student.grade && (
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                          <span className="text-gray-500">Grade:</span>
                          <p className="font-medium">{session.student.grade}</p>
                        </div>
                        <div>
                          <span className="text-gray-500">School:</span>
                          <p className="font-medium">{session.student.schoolName || 'Not provided'}</p>
                        </div>
                      </div>
                    )}

                    {session.student.interests && session.student.interests.length > 0 && (
                      <div className="mt-3">
                        <span className="text-gray-500 text-sm">Interests:</span>
                        <div className="flex flex-wrap gap-1 mt-1">
                          {session.student.interests.map((interest: string, index: number) => (
                            <span 
                              key={index}
                              className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full"
                            >
                              {interest}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Session Notes/Feedback */}
              {session.feedback && (
                <div>
                  <h4 className="font-medium text-gray-900 mb-2">Session Feedback</h4>
                  <div className="bg-gray-50 rounded-lg p-3">
                    <p className="text-sm text-gray-700">{session.feedback}</p>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Action buttons */}
          <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
            <button
              onClick={onClose}
              className="w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none sm:ml-3 sm:w-auto sm:text-sm"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}